import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import T from 'prop-types';

import fetchWrapper from 'utils/fetch';
import Icon from '@ably/ui/core/Icon';
import { convertKeysToCamelCase } from 'utils/strings';
import { locationDataProp, formFriendlyData } from './locationData';
import { upgradeFormValidator, stripeElementStyles, mapAccountErrors } from './utils';
import { ErrorMessage, Label, Field, Select } from './FormElements';

function UpgradeForm(options) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeElementErrors, setStripeElementErrors] = useState({ cardNumber: '', cardExpiry: '', cardCvc: '' });

  const countries = formFriendlyData(options.locationData.countries);
  const regions = formFriendlyData(options.locationData.usStates);
  const defaultCountry = countries.find((country) => country.value === options.countryCode) || { label: '', value: '' };
  const defaultRegion = regions.find((region) => region.value === options.region) || { label: '', value: '' };

  const initialFormValues = {
    billingEmail: options.billingEmail || '',
    country: defaultCountry,
    addressLineOne: options.addressLineOne || '',
    addressLineTwo: options.addressLineTwo || '',
    city: options.city || '',
    postCode: options.postCode || '',
    state: defaultRegion,
    companyName: options.companyName || '',
    vatNumber: options.vatNumber || '',
    acceptsTerms: options.acceptsTerms || false,
  };

  const onSubmit = async (values, { setErrors }) => {
    setIsSubmitting(true);
    if (!stripe || !elements) {
      setIsSubmitting(false);
      return;
    }

    const cardElement = elements.getElement('cardNumber');

    const stripeResponse = await stripe.confirmCardSetup(options.clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (stripeResponse.error) {
      setErrors({
        stripeErrorMessage: stripeResponse.error.message,
      });
      setIsSubmitting(false);
      return;
    }

    const data = {
      payg_subscription: {
        package: options.package,
        stripe_payment_method_id: stripeResponse.setupIntent.payment_method,
        company_name: values.companyName,
        billing_email: values.billingEmail,
        address_1: values.addressLineOne,
        address_2: values.addressLineTwo,
        city: values.city,
        region: values.state?.value,
        country_code: values.country?.value,
        postal_code: values.postCode,
        vat_number: values.vatNumber,
      },
    };

    try {
      const response = await fetchWrapper('/api/pricing/payg_subscription', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();

      if (response.status === 201) {
        const location = response.headers.get('location');
        window.location.assign(location);
        return;
      }

      const errors = {
        headerErrors: result.errors.header_errors,
        ...convertKeysToCamelCase(result.errors.user),
        ...mapAccountErrors(result.errors.account),
      };

      const newClientSecret = (await options.refetchClientSecret())?.client_secret;
      options.updateClientSecret(newClientSecret);

      setErrors(errors);
      setIsSubmitting(false);
    } catch (errors) {
      setErrors(errors);
      setIsSubmitting(false);
    }
  };

  const onStripeElementChange = ({ elementType, error }) => {
    setStripeElementErrors((elementErrors) => ({ ...elementErrors, [elementType]: error ? error.message : '' }));
  };

  return (
    <Formik
      initialValues={initialFormValues}
      validateOnChange={false}
      validateOnBlur={false}
      validate={upgradeFormValidator}
      onSubmit={onSubmit}
    >
      {({ setFieldValue, values, errors }) => (
        <Form className="flex flex-col gap-8 mb-10">
          <div>
            <h3 className="text-h3 font-medium pb-2">Billing details</h3>
            <p className="text-p3 font-medium text-dark-grey">Provide your business and payment details</p>
          </div>

          <div>
            <Label htmlFor="companyName">Company name</Label>
            <Field id="companyName" name="companyName" placeholder="Optional company name" />
            <p className="ui-text-p3 text-neutral-500 mt-1">
              An optional company name to be used for billing purposes. If left blank, your name will be used.
            </p>
          </div>

          <div>
            <Label htmlFor="billingEmail">Billing email</Label>
            <Field
              id="billingEmail"
              name="billingEmail"
              placeholder="Alternative billing email address"
              error={errors.billingEmail}
            />
            <ErrorMessage name="billingEmail" />
            <p className="ui-text-p3 text-neutral-500 mt-1">
              Invoices and billing notifications are sent to the account owner&apos;s email. Provide an alternative
              address here to send them elsewhere.
            </p>
          </div>

          <div>
            <Label>Billing address</Label>
            <div className="flex flex-col gap-2">
              <Select
                id="country"
                name="country"
                placeholder="Country"
                options={countries}
                onChange={(option) => setFieldValue('country', option)}
                defaultValue={defaultCountry.value ? defaultCountry : null}
              />
              <ErrorMessage name="country" />
              <Field name="addressLineOne" placeholder="Address" error={errors.addressLineOne} />
              <ErrorMessage name="addressLineOne" />
              <Field name="addressLineTwo" placeholder="Address line 2 (optional)" />
              <div className="flex gap-2 max-w-screen-sm">
                <div className="w-full">
                  <Field name="city" placeholder="City" error={errors.city} />
                  <ErrorMessage name="city" />
                </div>
                <div className="w-full">
                  <Field
                    name="postCode"
                    placeholder={values.country.value === 'US' ? 'Zip' : 'Post Code'}
                    error={errors.postCode}
                  />
                  <ErrorMessage name="postCode" />
                </div>
              </div>
              {values.country.value === 'US' ? (
                <>
                  <Select
                    name="state"
                    placeholder="State"
                    options={regions}
                    onChange={(option) => setFieldValue('state', option)}
                  />
                  <ErrorMessage name="state" />
                </>
              ) : null}
            </div>
          </div>

          <div>
            <Label htmlFor="vatNumber">VAT number</Label>
            <Field id="vatNumber" name="vatNumber" placeholder="GB123456789" />
          </div>

          <div className="flex gap-2">
            <Label htmlFor="cardInformation">
              Card information
              {errors.stripeErrorMessage && <p className="text-gui-error">{errors.stripeErrorMessage}</p>}
              {errors.paymentMethod && <p className="text-gui-error">{errors.paymentMethod}</p>}
              <CardNumberElement
                id="stripeNumberElement"
                className="focus:outline-gui-focus border border-mid-grey ui-input"
                options={stripeElementStyles}
                onChange={onStripeElementChange}
              />
              {stripeElementErrors.cardNumber ? (
                <span className="text-gui-error">{stripeElementErrors.cardNumber}</span>
              ) : null}
              <div className="flex gap-2 max-w-screen-sm">
                <div className="w-full">
                  <CardExpiryElement
                    id="stripeExpiryElement"
                    className="focus:outline-gui-focus border border-mid-grey ui-input"
                    options={stripeElementStyles}
                    onChange={onStripeElementChange}
                  />
                  {stripeElementErrors.cardExpiry ? (
                    <span className="text-gui-error">{stripeElementErrors.cardExpiry}</span>
                  ) : null}
                </div>
                <div className="w-full">
                  <CardCvcElement
                    id="stripeCvcElement"
                    className="focus:outline-gui-focus border border-mid-grey ui-input"
                    options={stripeElementStyles}
                    onChange={onStripeElementChange}
                  />
                  {stripeElementErrors.cardCvc ? (
                    <span className="text-gui-error">{stripeElementErrors.cardCvc}</span>
                  ) : null}
                </div>
              </div>
            </Label>
          </div>

          <div className="flex items-start cursor-pointer">
            <div className="ui-checkbox-p1 relative">
              <input
                data-ui-checkbox-native=""
                type="checkbox"
                id="userAcceptsTerms"
                name="userAcceptsTerms"
                className="ui-checkbox-input"
                defaultValue={values.acceptsTerms}
                onClick={() => {
                  setFieldValue('acceptsTerms', !values.acceptsTerms);
                }}
              />
              <div data-ui-checkbox-styled="" className="ui-checkbox-styled">
                <Icon name="icon-gui-check-outline" size="1rem" additionalCSS="ui-checkbox-styled-tick" />
              </div>
            </div>

            <div className="font-sans">
              <label htmlFor="userAcceptsTerms" className="ui-text-p3 pb-1 text-dark-grey w-full gap-1 cursor-pointer">
                I agree to the{' '}
                <a href="/terms" target="_blank">
                  Terms of Service
                </a>
              </label>
            </div>
          </div>
          <ErrorMessage name="acceptsTerms" />

          <button id="confirmPurchaseBtn" type="submit" className="flex ui-btn self-end" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting.....' : 'Confirm purchase'}
          </button>
        </Form>
      )}
    </Formik>
  );
}

UpgradeForm.propTypes = {
  companyName: T.string,
  clientSecret: T.string,
  locationData: locationDataProp,
  billingEmail: T.string,
  addressLineOne: T.string,
  addressLineTwo: T.string,
  city: T.string,
  postCode: T.string,
  state: T.string,
  countryCode: T.string,
  vatNumber: T.string,
  refetchClientSecret: T.func,
  updateClientSecret: T.func,
};

export { UpgradeForm };
